import { Trans } from '@lingui/react/macro'
import { Alert, AlertDescription } from 'designSystem/components/Alert'
import { Box } from 'designSystem/components/Box'
import { Text } from 'designSystem/components/Text'
import {
  TextHighlight,
  type TextHighlightColor,
} from 'designSystem/components/TextHighlight'
import type { CitationFailureReason } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { CircleAlert } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'

const PAGE_FRAME_BACKGROUND = {
  muted: 'var(--muted)',
  page: 'var(--muted)',
} as const

const ThumbnailFrame = styled.div<{ $height: string; $background: string }>`
  display: flex;
  height: ${({ $height }) => $height};
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 0.25rem;
  background-color: ${({ $background }) => $background};
  padding: 0.375rem;

  > div,
  > img {
    width: 100%;
    height: 100%;
  }
`

const QuotesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  max-height: 10rem;
  overflow-y: auto;
`

type QuotePalette = Extract<TextHighlightColor, 'green' | 'amber'>

const QuoteText = styled.div<{ $palette: QuotePalette }>`
  font-size: 0.75rem;
  font-style: italic;
  line-height: 1.4;
  overflow-wrap: anywhere;
  white-space: pre-line;
  padding: 0.125rem 0.25rem;
  color: ${({ $palette }) =>
    $palette === 'amber' ? 'var(--warning-foreground)' : 'inherit'};
`

interface EvidencePreviewProps {
  title: string
  quotes: string[]
  quotePalette?: QuotePalette
  failureReason?: CitationFailureReason | null
  pageHeight?: string
  pageFrame?: keyof typeof PAGE_FRAME_BACKGROUND
  page?: React.ReactNode
}

/** Title, framed page, and quotes — the DocChat evidence preview body. */
export function EvidencePreview({
  title,
  quotes,
  quotePalette = 'green',
  failureReason = null,
  pageHeight = '8rem',
  pageFrame = 'muted',
  page,
}: EvidencePreviewProps): React.ReactElement {
  const isFailed = failureReason !== null && failureReason !== undefined

  return (
    <Box display='flex' direction='column' gap='sm'>
      <Text variant='caption' weight='medium'>
        {title}
      </Text>
      {page ? (
        <ThumbnailFrame
          $height={pageHeight}
          $background={PAGE_FRAME_BACKGROUND[pageFrame]}>
          {page}
        </ThumbnailFrame>
      ) : null}
      {quotes.length > 0 ? (
        <QuotesList>
          {quotes.map((quote) => (
            <QuoteText key={quote} $palette={quotePalette}>
              <TextHighlight color={quotePalette}>
                &ldquo;{quote}&rdquo;
              </TextHighlight>
            </QuoteText>
          ))}
        </QuotesList>
      ) : null}
      {isFailed ? (
        <Alert variant='warning' size='sm'>
          <CircleAlert />
          <AlertDescription>
            <Trans>
              Cantilever was unable to verify this claim. Please verify the
              source.
            </Trans>
          </AlertDescription>
        </Alert>
      ) : null}
    </Box>
  )
}
